import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Patient,
  type PatientManagerQuery,
} from '../store/slices/patientsSlice';
import {
  Card,
  Button,
  CardContent,
  FilterToolbarSearch,
  FormLabel,
  filterToolbarClinicClassName,
  filterToolbarSearchGrowClassName,
  type ReportTableSort,
} from '@roger/ui-components';
import { PatientManagerTable } from '../components/patient/PatientManagerTable';
import {
  PATIENTMANAGERSORTBY_VALUES,
  type PatientManagerSortBy,
} from '../services/api/models/PatientManagerSortBy';
import {
  ALL_CLINICS,
  useAccessibleClinics,
} from '../hooks/useAccessibleClinics';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import { useDebounce } from '../hooks/useDebounce';
import { canAgencyImportFromExternalEmr } from '../hooks/useExternalEmrImport';
import { useSyncReviewReadable } from '../hooks/useSyncReviewReadable';
import {
  EXTERNAL_EMR_READ_PERMISSIONS,
  FeatureFlag,
  PermissionSlug,
} from '../constants/featureFlags';
import { useExternalEmrSummary } from '../hooks/useExternalEmrSummary';
import { ExternalEmrCensusTable } from '../components/patient/externalEmr/ExternalEmrCensusTable';
import { ExternalEmrSelectionBar } from '../components/patient/externalEmr/ExternalEmrSelectionBar';
import { ExternalEmrSummaryHeader } from '../components/patient/externalEmr/ExternalEmrSummaryHeader';
import { isExternalPatient } from '../components/patient/externalEmr/externalEmrSelection';
import { ExternalEmrStatusChips } from '../components/patient/externalEmr/ExternalEmrStatusChips';
import {
  EXTERNAL_EMR_EMPTY,
  EXTERNAL_EMR_EMPTY_VIEW_ONLY,
  SEE_THE_CHARTS_LABEL,
  statusEmptyLine,
  takeOverPendingLine,
} from '../components/patient/externalEmr/externalEmrPatientCopy';
import { useTakeOverAvailable } from '../hooks/useTakeOverAvailable';
import { useTakeOverPendingSignal } from '../hooks/useTakeOverPendingSignal';
import type { ExternalEmrListStatusFilter } from '../services/externalEmrPatients';
import {
  PatientManagerListFilters,
  type PatientManagerFilterOption,
} from '../components/patient/PatientManagerListFilters';
import {
  ExportMenuButton,
  UserFacingExportError,
} from '../components/common/ExportMenuButton';
import { exportPatients } from '../services/api';
import { getUserFacingApiMessageAsync } from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import { getBusinessToday } from '../utils/dateUtils';
import { estimateExportSeconds } from '../utils/exportEstimate';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Import settles close together share one background reload. */
const EXTERNAL_RELOAD_DEBOUNCE_MS = 1_000;

const isSortKey = (id: string): id is PatientManagerSortBy =>
  (PATIENTMANAGERSORTBY_VALUES as readonly string[]).includes(id);

const DOWNLOAD_FAILED = 'Unable to download. Please try again.';

const Patients: React.FC = () => {
  const navigate = useNavigate();
  const {
    patients,
    isPatientsLoading: loading,
    error,
    fetchPatients,
    searchTerm,
    setSearchTerm,
    showInactive,
    setShowInactive,
    hideTest,
    setHideTest,
    patientManagerHideImportedPatients,
    setPatientManagerHideImportedPatients,
    sortBy,
    sortOrder,
    setSort,
    insuranceIds,
    setInsuranceIds,
    cities,
    setCities,
    cityOptions,
    agencyInsurances,
    agencyInsurancesLoading,
    agencyInsurancesError,
    agencyInsurancesLastFetched,
    fetchAgencyInsurances,
    alphaCounts,
    currentPage,
    pageSize,
    totalPatients,
    surnameInitial,
    setCurrentPage,
    setSurnameInitial,
    externalEmrView,
    setExternalEmrView,
    externalEmrStatus,
    setExternalEmrStatus,
    externalEmrStatusCounts,
    agency,
    hasPermission,
    hasAnyPermission,
    importFlagOn,
  } = useStore(
    useShallow(state => ({
      patients: state.patients,
      isPatientsLoading: state.isPatientsLoading,
      error: state.error,
      fetchPatients: state.fetchPatients,
      searchTerm: state.searchTerm,
      setSearchTerm: state.setSearchTerm,
      showInactive: state.showInactive,
      setShowInactive: state.setShowInactive,
      hideTest: state.hideTest,
      setHideTest: state.setHideTest,
      patientManagerHideImportedPatients:
        state.patientManagerHideImportedPatients,
      setPatientManagerHideImportedPatients:
        state.setPatientManagerHideImportedPatients,
      sortBy: state.patientManagerSortBy,
      sortOrder: state.patientManagerSortOrder,
      setSort: state.setPatientManagerSort,
      insuranceIds: state.patientManagerInsuranceIds,
      setInsuranceIds: state.setPatientManagerInsuranceIds,
      cities: state.patientManagerCities,
      setCities: state.setPatientManagerCities,
      cityOptions: state.patientManagerCityOptions,
      agencyInsurances: state.agencyInsurances,
      agencyInsurancesLoading: state.agencyInsurancesLoading,
      agencyInsurancesError: state.agencyInsurancesError,
      agencyInsurancesLastFetched: state.agencyInsurancesLastFetched,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
      alphaCounts: state.alphaCounts,
      currentPage: state.currentPage,
      pageSize: state.pageSize,
      totalPatients: state.totalPatients,
      surnameInitial: state.surnameInitial,
      setCurrentPage: state.setCurrentPage,
      setSurnameInitial: state.setSurnameInitial,
      externalEmrView: state.patientManagerExternalEmrView,
      setExternalEmrView: state.setPatientManagerExternalEmrView,
      externalEmrStatus: state.patientManagerExternalEmrStatus,
      setExternalEmrStatus: state.setPatientManagerExternalEmrStatus,
      externalEmrStatusCounts: state.externalEmrStatusCounts,
      agency: state.user?.agency,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      importFlagOn: state.hasFeatureFlag(
        FeatureFlag.EXTERNAL_EMR_IMPORT,
        'workos'
      ),
    }))
  );

  const {
    clinics: clinicOptions,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
    shouldShowPicker,
  } = useAccessibleClinics({ persistKey: 'patientList' });

  const [searchInput, setSearchInput] = useState(searchTerm);
  const safePatients = patients || [];
  const totalPages = Math.max(1, Math.ceil(totalPatients / pageSize));
  const firstShown = totalPatients === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastShown = Math.min(currentPage * pageSize, totalPatients);

  const debouncedSearch = useDebounce((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, 350);

  // External EMR patients: shells known from the census, and imported charts.
  const agencyCanImport = canAgencyImportFromExternalEmr(agency, importFlagOn);
  // Take Over v2's sync review: readable with its flag on, syncing or not.
  const showSyncReview = useSyncReviewReadable();
  const inExternalEmrView = agencyCanImport && externalEmrView;
  /** Render gate for start, cancel and census refresh (three-gate rule). */
  const canStartImport =
    agencyCanImport && hasPermission(PermissionSlug.CHART_IMPORT_START);
  /** Render gate for the census row's charts-from day (three-gate rule). */
  const canPlanChartsFrom =
    agencyCanImport && hasPermission(PermissionSlug.EPISODE_TAKE_OVER);
  const clinicFilter = isAllClinics ? null : selectedClinicId;
  // Take Over v2: the row's Confirm, the Needs Take Over chip and both
  // "need Take Over" lines. Off, there is no Take Over to press.
  const takeOverAvailable = useTakeOverAvailable();
  // The summary route's read gate: without it the read would only 403.
  const canReadExternalEmr = hasAnyPermission([
    ...EXTERNAL_EMR_READ_PERMISSIONS,
  ]);
  const takeOverSignal = useTakeOverPendingSignal({
    enabled:
      agencyCanImport &&
      canReadExternalEmr &&
      takeOverAvailable &&
      !inExternalEmrView,
    clinicId: clinicFilter,
  });
  const showStatus = useCallback(
    (status: ExternalEmrListStatusFilter | null) => {
      setExternalEmrStatus(status);
      setCurrentPage(1);
    },
    [setExternalEmrStatus, setCurrentPage]
  );
  // "N charts need Take Over" opens exactly those charts: the search and the
  // letter would narrow the list below the count, so both clear.
  const showNeedsTakeOver = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
    setSurnameInitial('');
    setExternalEmrView(true);
    showStatus('needs_take_over');
  }, [setSearchTerm, setSurnameInitial, setExternalEmrView, showStatus]);

  // Selection lives on the visible page; any change of page or filter clears it.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    currentPage,
    inExternalEmrView,
    externalEmrStatus,
    searchTerm,
    clinicFilter,
    surnameInitial,
    showInactive,
    hideTest,
  ]);
  const externalPatients = useMemo(
    () => (inExternalEmrView ? safePatients.filter(isExternalPatient) : []),
    [inExternalEmrView, safePatients]
  );
  const selectedPatients = useMemo(
    () => externalPatients.filter(p => selectedIds.has(p.id)),
    [externalPatients, selectedIds]
  );
  const toggleSelected = useCallback((patient: { id: string }) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(patient.id)) next.delete(patient.id);
      else next.add(patient.id);
      return next;
    });
  }, []);
  const keepOnlySelected = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);
  // The one query this page shows. Every fetch (filters, Try Again, the
  // External EMR reloads) sends all of it, so nothing falls back to state.
  const pageQuery = useMemo<PatientManagerQuery>(
    () => ({
      page: currentPage,
      search: searchTerm,
      clinic_id: isAllClinics ? null : selectedClinicId,
      surname_initial: surnameInitial || null,
      show_inactive: showInactive,
      hide_test: hideTest,
      hide_imported_patients: patientManagerHideImportedPatients,
      // Absent outside the view, so the ordinary request is unchanged.
      // fetchPatients drops the three population filters for this view.
      // The sort and the table filters belong to the Roger charts table.
      ...(inExternalEmrView
        ? {
            external_emr: 'all' as const,
            external_emr_status: externalEmrStatus,
          }
        : {
            sort_by: sortBy,
            sort_order: sortOrder,
            insurance_ids: insuranceIds,
            cities,
          }),
    }),
    [
      cities,
      currentPage,
      inExternalEmrView,
      insuranceIds,
      externalEmrStatus,
      hideTest,
      isAllClinics,
      patientManagerHideImportedPatients,
      searchTerm,
      selectedClinicId,
      showInactive,
      sortBy,
      sortOrder,
      surnameInitial,
    ]
  );

  // The Excel file holds every row the table's filters and sort match: the
  // page query without the page. Null sorts mean the server default.
  const handleExport = useCallback(async () => {
    const fail = async (cause: unknown): Promise<never> => {
      console.error('Failed to export patients', cause);
      throw new UserFacingExportError(
        await getUserFacingApiMessageAsync(cause, DOWNLOAD_FAILED)
      );
    };
    const response = await exportPatients({
      query: {
        search: pageQuery.search,
        clinic_id: pageQuery.clinic_id,
        surname_initial: pageQuery.surname_initial,
        show_inactive: pageQuery.show_inactive,
        hide_test: pageQuery.hide_test,
        hide_imported_patients: pageQuery.hide_imported_patients,
        sort_by: pageQuery.sort_by ?? undefined,
        sort_order: pageQuery.sort_order ?? undefined,
        insurance_ids: pageQuery.insurance_ids,
        cities: pageQuery.cities,
      },
      responseType: 'blob' as const,
    }).catch(fail);
    if (response.error || !response.data) return fail(response);
    downloadResponseAsFile(response, `patients-${getBusinessToday()}.xlsx`);
  }, [pageQuery]);

  // Counts and the background census read. A finished read reloads the list.
  const reloadListAfterCensus = useCallback(() => {
    void fetchPatients(pageQuery);
  }, [fetchPatients, pageQuery]);
  const {
    summary: externalSummary,
    failed: externalSummaryFailed,
    reload: loadExternalSummary,
    censusStarted,
    awaitingCensus,
  } = useExternalEmrSummary({
    enabled: inExternalEmrView,
    clinicId: clinicFilter,
    onCensusSettled: reloadListAfterCensus,
  });
  // The agency's stored External EMR login or consent cannot carry an import:
  // the summary header says why, and no Import control is offered.
  const importBlocked = externalSummary?.import_ready === false;
  const importBlockedReason = importBlocked
    ? (externalSummary?.import_blocked_reason ?? null)
    : null;
  const canStartNow = canStartImport && !importBlocked;
  // Until the summary answers, readiness is unknown: show neither Import nor
  // the reason, so Import never flashes before a reason replaces it. A failed
  // summary read counts as ready; the press itself still answers.
  const importReadinessPending = !externalSummary && !externalSummaryFailed;

  // An import started, settled or was canceled: counts and cards follow. A
  // bulk run settles many cards together, so they share one background
  // reload. It reads the page and filters when it fires, so a change made
  // while it waits is never undone.
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestReload = useRef({ pageQuery, loadExternalSummary });
  useEffect(() => {
    latestReload.current = { pageQuery, loadExternalSummary };
  }, [pageQuery, loadExternalSummary]);
  const reloadExternal = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null;
      const latest = latestReload.current;
      void latest.loadExternalSummary();
      // Always with counts: a reload from page 2 must still move the chips.
      void fetchPatients({
        ...latest.pageQuery,
        background: true,
        include_metadata: true,
      });
    }, EXTERNAL_RELOAD_DEBOUNCE_MS);
  }, [fetchPatients]);
  useEffect(
    () => () => {
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
    },
    []
  );
  // The view belongs to the agency it was turned on in.
  const agencyId = agency?.id ?? null;
  const viewAgency = useRef(agencyId);
  useEffect(() => {
    if (viewAgency.current === agencyId) return;
    viewAgency.current = agencyId;
    setExternalEmrView(false);
  }, [agencyId, setExternalEmrView]);

  // Keep matching, totals, and A-Z availability on the bounded server query.
  useEffect(() => {
    void fetchPatients(pageQuery);
  }, [fetchPatients, pageQuery]);

  // The Insurance filter lists the agency's payers; the store caches them.
  // One request per session: an empty answer is not retried, and a failed
  // one is retried only from the filter's own Retry item.
  useEffect(() => {
    if (inExternalEmrView || agencyInsurances.length > 0) return;
    if (
      !agencyInsurancesLoading &&
      agencyInsurancesLastFetched === null &&
      agencyInsurancesError === null
    ) {
      void fetchAgencyInsurances();
    }
  }, [
    agencyInsurances.length,
    agencyInsurancesError,
    agencyInsurancesLastFetched,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
    inExternalEmrView,
  ]);
  const insuranceOptions = useMemo<PatientManagerFilterOption[]>(
    () =>
      [...agencyInsurances]
        .sort(
          (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
        )
        .map(insurance => ({ value: insurance.id, label: insurance.name })),
    [agencyInsurances]
  );

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder }
    : null;
  const onSortingChange = useCallback(
    (next: ReportTableSort | null) => {
      setSort(next && isSortKey(next.id) ? next.id : null, next?.direction);
      setCurrentPage(1);
    },
    [setSort, setCurrentPage]
  );
  const openChart = useCallback(
    (patient: Patient) => navigate(`/patients/${patient.id}`),
    [navigate]
  );
  // The export route is gated on patient-mgr:access; a user who reached the
  // page through emr:manage alone would only get a 403 from the button.
  const canExport = hasPermission(PermissionSlug.PATIENT_MANAGER);

  return (
    <div className='space-y-6'>
      {/* Error Display */}
      {error && (
        <Card className='border-destructive bg-destructive/10'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertTriangle className='h-4 w-4' />
              <span className='font-medium'>Error loading patients</span>
            </div>
            <p className='text-sm text-destructive/80 mt-1'>{error}</p>
            <Button
              variant='outline'
              size='sm'
              className='mt-2'
              onClick={() => fetchPatients(pageQuery)}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Patient List Controls */}
      <Card>
        <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4'>
          <FilterToolbarSearch
            value={searchInput}
            onChange={value => {
              setSearchInput(value);
              debouncedSearch(value);
            }}
            label='Search'
            placeholder='Name or MRN...'
            // Shared grow recipe; basis-auto avoids flex-col height stretch below sm.
            containerClassName={filterToolbarSearchGrowClassName}
            className='w-full'
          />
          {shouldShowPicker && (
            <div className={filterToolbarClinicClassName}>
              {/* Own FormLabel so Clinic matches Search (text-sm); ClinicSelect's
                  built-in label is text-xs via VirtualizedFormSelect. */}
              <FormLabel htmlFor='patient-clinic-filter' showTooltip={false}>
                Clinic
              </FormLabel>
              <ClinicSelect
                inputId='patient-clinic-filter'
                className='w-full'
                clinics={clinicOptions}
                includeAllOption
                allOptionValue={ALL_CLINICS}
                value={selectedClinicId}
                onChange={e => {
                  setSelectedClinicId(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder='Filter by clinic'
              />
            </div>
          )}
          <PatientManagerListFilters
            showInactive={showInactive}
            hideTest={hideTest}
            hideImported={patientManagerHideImportedPatients}
            onShowInactive={show => {
              setShowInactive(show);
              setCurrentPage(1);
            }}
            onHideTest={hide => {
              setHideTest(hide);
              setCurrentPage(1);
            }}
            onHideImported={hide => {
              setPatientManagerHideImportedPatients(hide);
              setCurrentPage(1);
            }}
            insuranceOptions={insuranceOptions}
            insuranceError={agencyInsurancesError}
            onRetryInsurances={() => void fetchAgencyInsurances()}
            insuranceIds={insuranceIds}
            onInsuranceIds={ids => {
              setInsuranceIds(ids);
              setCurrentPage(1);
            }}
            cityOptions={cityOptions}
            cities={cities}
            onCities={next => {
              setCities(next);
              setCurrentPage(1);
            }}
            showViewSelector={agencyCanImport}
            externalEmrView={externalEmrView}
            onExternalEmrView={on => {
              setExternalEmrView(on);
              setCurrentPage(1);
            }}
          />
        </div>
        {/* Alphabetical Navigation */}
        <div className='mt-4 flex flex-wrap gap-1'>
          {alphabet.map(letter => (
            <Button
              key={letter}
              variant={surnameInitial === letter ? 'default' : 'outline'}
              size='sm'
              className='px-2 py-1 text-xs'
              onClick={() => {
                setSurnameInitial(surnameInitial === letter ? '' : letter);
                setCurrentPage(1);
              }}
              disabled={!alphaCounts[letter] && surnameInitial !== letter}
            >
              {letter}
            </Button>
          ))}
        </div>
      </Card>

      {inExternalEmrView && (
        <ExternalEmrSummaryHeader
          summary={externalSummary}
          failed={externalSummaryFailed}
          canRefresh={canStartImport}
          onStarted={censusStarted}
          awaiting={awaitingCensus}
          canTakeOver={takeOverAvailable}
          onShowTakeOver={showNeedsTakeOver}
        />
      )}

      {!inExternalEmrView && takeOverSignal && (
        <div
          className='flex flex-col gap-1 rounded-md border bg-white p-3 text-sm sm:flex-row sm:items-center sm:gap-2'
          data-testid='take-over-signal'
        >
          <span>
            {takeOverPendingLine(takeOverSignal.count, takeOverSignal.lastDay)}
          </span>
          <Button
            variant='link'
            size='sm'
            className='self-start sm:self-auto'
            onClick={showNeedsTakeOver}
            data-testid='take-over-signal-open'
          >
            {SEE_THE_CHARTS_LABEL}
          </Button>
        </div>
      )}

      {/* Patient list */}
      <Card>
        <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <div className='text-xs text-muted-foreground'>
            {!loading &&
              `Showing ${firstShown}-${lastShown} of ${totalPatients} patients`}
          </div>
          <div
            className='flex flex-wrap items-center gap-2'
            data-testid='patient-manager-actions'
          >
            {!inExternalEmrView && canExport && (
              <ExportMenuButton
                onExport={handleExport}
                formats={['xlsx']}
                label='Download to Excel'
                disabled={loading || totalPatients === 0}
                estimateSeconds={estimateExportSeconds(totalPatients)}
              />
            )}
          </div>
        </div>
        {inExternalEmrView && (
          <ExternalEmrStatusChips
            counts={externalEmrStatusCounts}
            selected={externalEmrStatus}
            onSelect={showStatus}
            canTakeOver={takeOverAvailable}
          />
        )}
        {inExternalEmrView ? (
          !loading && externalPatients.length > 0 ? (
            <ExternalEmrCensusTable
              patients={externalPatients}
              canStart={canStartImport}
              canPlan={canPlanChartsFrom}
              canTakeOver={takeOverAvailable}
              startBlocked={importBlocked || importReadinessPending}
              selectable={canStartImport}
              selectedIds={selectedIds}
              onToggleSelected={toggleSelected}
              onOpen={openChart}
              onChanged={reloadExternal}
            />
          ) : (
            <div className='text-xs text-muted-foreground'>
              {loading
                ? 'Loading patients...'
                : externalEmrStatus
                  ? statusEmptyLine(externalEmrStatus)
                  : canStartNow
                    ? EXTERNAL_EMR_EMPTY
                    : EXTERNAL_EMR_EMPTY_VIEW_ONLY}
            </div>
          )
        ) : (
          <PatientManagerTable
            patients={safePatients}
            isLoading={loading}
            sorting={sorting}
            onSortingChange={onSortingChange}
            onOpen={openChart}
            showSyncReview={showSyncReview}
          />
        )}
        {!loading && totalPatients > 0 && (
          <div className='mt-4 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between'>
            <span className='text-muted-foreground'>
              Page {currentPage} of {totalPages}
            </span>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Previous
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
      {inExternalEmrView && canStartImport && (
        <ExternalEmrSelectionBar
          items={selectedPatients}
          onClear={() => setSelectedIds(new Set())}
          onRemoveItem={toggleSelected}
          onKeepOnly={keepOnlySelected}
          onChanged={reloadExternal}
          startBlockedReason={importBlockedReason}
          startPending={importReadinessPending}
        />
      )}
    </div>
  );
};

export default Patients;
